import { roundMoney } from '../../utils/money';
import type { WorkflowInvoice, WorkflowInvoiceLine } from '../../types/workflowInvoice';

const known = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** OCR amounts remain evidence. Fill only missing values with a known VAT basis. */
export function completeInvoiceAmounts(invoice: WorkflowInvoice): WorkflowInvoice {
  if (invoice.status !== 'DRAFT') return invoice;
  const lines = invoice.lines.map(line => {
    if (!known(line.netAmount)) return line;
    const vatAmount = line.vatAmount ?? (known(line.taxRate) ? roundMoney(line.netAmount * line.taxRate / 100) : null);
    if (!known(vatAmount)) return line;
    return { ...line, vatAmount, grossAmount: line.grossAmount ?? roundMoney(line.netAmount + vatAmount) };
  });
  const sum = (key: 'netAmount' | 'vatAmount' | 'grossAmount') => lines.length && lines.every(line => known(line[key]))
    ? roundMoney(lines.reduce((total, line) => total + line[key]!, 0)) : null;
  return { ...invoice, lines, totalNet: invoice.totalNet ?? sum('netAmount'), totalVat: invoice.totalVat ?? sum('vatAmount'), totalGross: invoice.totalGross ?? sum('grossAmount') };
}

/** A deliberate price/net/VAT edit recomputes its dependants; physical unit changes do not. */
export function editInvoiceAmounts(line: WorkflowInvoiceLine, patch: Partial<WorkflowInvoiceLine>): WorkflowInvoiceLine {
  const next = { ...line, ...patch };
  if ('unitPrice' in patch && known(next.unitPrice) && known(next.commercialQuantity)) {
    const undiscounted = next.unitPrice * next.commercialQuantity;
    next.netAmount = roundMoney(undiscounted - (next.discountAmount ?? undiscounted * (next.discountPercent ?? 0) / 100));
  }
  if (('netAmount' in patch || 'taxRate' in patch || 'unitPrice' in patch) && known(next.netAmount) && known(next.taxRate)) {
    next.vatAmount = roundMoney(next.netAmount * next.taxRate / 100);
    next.grossAmount = roundMoney(next.netAmount + next.vatAmount);
  } else if ('vatAmount' in patch && known(next.netAmount) && known(next.vatAmount)) {
    next.grossAmount = roundMoney(next.netAmount + next.vatAmount);
  }
  return next;
}
